import { computed, signal } from "@preact/signals";
import type {
  AttachmentInfo,
  CamoInfo,
  EquipmentInfo,
  FullCatalog,
  FullModeInfo,
  FullWeaponInfo,
  GameMode,
  MapInfo,
  PerkInfo,
} from "../api/types";
import { getMeta } from "../api/client";
import { defaultModeName } from "../api/modes";

/** Content catalog from GET /api/meta; null until boot has loaded it. */
export const meta = signal<FullCatalog | null>(null);

export interface MetaIndex {
  weapons: Map<string, FullWeaponInfo>;
  attachments: Map<string, AttachmentInfo>;
  perks: Map<string, PerkInfo>;
  equipment: Map<string, EquipmentInfo>;
  camos: Map<string, CamoInfo>;
  maps: Map<string, MapInfo>;
  modes: Map<GameMode, FullModeInfo>;
}

function indexBy<K, T>(items: readonly T[], key: (item: T) => K): Map<K, T> {
  const m = new Map<K, T>();
  for (const item of items) m.set(key(item), item);
  return m;
}

export const metaIndex = computed<MetaIndex | null>(() => {
  const c = meta.value;
  if (!c) return null;
  return {
    weapons: indexBy(c.weapons, (w) => w.id),
    attachments: indexBy(c.attachments, (a) => a.id),
    perks: indexBy(c.perks, (p) => p.id),
    equipment: indexBy(c.equipment, (e) => e.id),
    camos: indexBy(c.camos, (x) => x.id),
    maps: indexBy(c.maps, (m) => m.id),
    modes: indexBy(c.modes, (m) => m.mode),
  };
});

export async function loadMeta(signal?: AbortSignal): Promise<void> {
  meta.value = await getMeta(signal);
}

/** Display name of a mode: the catalog's, else the built-in default. */
export function modeName(mode: GameMode): string {
  return metaIndex.value?.modes.get(mode)?.name ?? defaultModeName(mode);
}

/** Map and weapon names fall back to the raw id (catalog not loaded or id unknown). */
export function mapName(id: string): string {
  return metaIndex.value?.maps.get(id)?.name ?? id;
}

export function weaponName(id: string): string {
  return metaIndex.value?.weapons.get(id)?.name ?? id;
}

/** The catalog for screens (same contract as `requireMe`: it is loaded before any screen renders). */
export function requireMeta(): FullCatalog {
  const value = meta.value;
  if (!value) throw new Error("requireMeta: catalog not loaded");
  return value;
}

export function requireMetaIndex(): MetaIndex {
  const value = metaIndex.value;
  if (!value) throw new Error("requireMetaIndex: catalog not loaded");
  return value;
}
